import React, { ReactNode, useCallback, useState } from 'react';
import { Sash } from './Sash';
import { useDocArea } from '../../contexts/DocAreaContext';
import { useRoomMembersCtx } from '../../contexts/RoomMembersContext';
import { useSetting } from '../../state/hooks/settings';
import { settingsAtom } from '../../state/settings';
import './curve-theme.css';

const CASE_LIST_DEFAULT_WIDTH = 320;
const CASE_LIST_MIN_WIDTH = 0;
const CASE_LIST_MAX_WIDTH = 500;
const MESSAGE_AREA_MIN_WIDTH = 300;
const MEMBERS_DEFAULT_WIDTH = 240;
const MEMBERS_MIN_WIDTH = 160;
const MEMBERS_MAX_WIDTH = 400;
const DOC_AREA_DEFAULT_WIDTH = 400;
const DOC_AREA_MIN_WIDTH = 0;
const DOC_AREA_MAX_WIDTH = 800;

type MainContentWithPanelsProps = {
  caseListPanel: ReactNode;
  messageArea: ReactNode;
  membersPanel?: ReactNode;
  docArea?: ReactNode;
};

export function MainContentWithPanels({
  caseListPanel,
  messageArea,
  membersPanel,
  docArea,
}: MainContentWithPanelsProps) {
  const { isDocAreaOpen, closeDocArea } = useDocArea();
  const { room } = useRoomMembersCtx();
  const [isDrawer] = useSetting(settingsAtom, 'isPeopleDrawer');
  const showMembersPanel = !!membersPanel && !!room && isDrawer;

  const [caseListWidth, setCaseListWidth] = useState(CASE_LIST_DEFAULT_WIDTH);
  const [membersWidth, setMembersWidth] = useState(MEMBERS_DEFAULT_WIDTH);
  const [docAreaWidth, setDocAreaWidth] = useState(DOC_AREA_DEFAULT_WIDTH);

  const effectiveCaseListWidth = isDocAreaOpen ? 0 : caseListWidth;

  const handleCaseListSashResize = useCallback((delta: number) => {
    setCaseListWidth((prev) => {
      const next = prev + delta;
      return Math.max(CASE_LIST_MIN_WIDTH, Math.min(CASE_LIST_MAX_WIDTH, next));
    });
  }, []);

  const handleMembersSashResize = useCallback((delta: number) => {
    setMembersWidth((prev) => {
      const next = prev - delta;
      return Math.max(MEMBERS_MIN_WIDTH, Math.min(MEMBERS_MAX_WIDTH, next));
    });
  }, []);

  const handleDocAreaSashResize = useCallback(
    (delta: number) => {
      setDocAreaWidth((prev) => {
        const next = prev - delta;
        if (next <= DOC_AREA_MIN_WIDTH) {
          closeDocArea();
          return DOC_AREA_DEFAULT_WIDTH;
        }
        return Math.min(DOC_AREA_MAX_WIDTH, next);
      });
    },
    [closeDocArea]
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: 'var(--curve-bg-white, #FFFFFF)',
      }}
    >
      {/* 左欄：案件列表 */}
      <div
        style={{
          width: `${effectiveCaseListWidth}px`,
          minWidth: 0,
          flexShrink: 0,
          overflow: 'hidden',
          transition: 'width var(--curve-transition-panel, 300ms ease)',
          background: 'var(--curve-bg-white, #FFFFFF)',
          borderRight: effectiveCaseListWidth > 0
            ? '1px solid var(--curve-border, #E5E5E5)'
            : 'none',
        }}
      >
        {caseListPanel}
      </div>

      {/* Sash：左欄 ↔ 中欄（資訊面板關閉時顯示） */}
      {!isDocAreaOpen && (
        <Sash onResize={handleCaseListSashResize} direction="horizontal" />
      )}

      {/* 中欄：訊息區 */}
      <div
        style={{
          flex: 1,
          minWidth: `${MESSAGE_AREA_MIN_WIDTH}px`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--curve-bg-white, #FFFFFF)',
        }}
      >
        {messageArea}
      </div>

      {/* Sash + 成員面板（聊天室開啟且 isPeopleDrawer 啟用時顯示） */}
      {showMembersPanel && (
        <>
          <Sash onResize={handleMembersSashResize} direction="horizontal" />
          <div
            style={{
              width: `${membersWidth}px`,
              minWidth: 0,
              flexShrink: 0,
              overflow: 'hidden',
              borderLeft: '1px solid var(--curve-border, #E5E5E5)',
              background: 'var(--curve-bg-white, #FFFFFF)',
            }}
          >
            {membersPanel}
          </div>
        </>
      )}

      {/* Sash：中欄 ↔ 資訊面板（資訊面板開啟時顯示） */}
      {isDocAreaOpen && (
        <Sash onResize={handleDocAreaSashResize} direction="horizontal" />
      )}

      {/* 右欄：資訊面板 */}
      <div
        style={{
          width: isDocAreaOpen ? `${docAreaWidth}px` : '0px',
          minWidth: 0,
          flexShrink: 0,
          overflow: 'hidden',
          transition: 'width var(--curve-transition-panel, 300ms ease)',
          borderLeft: isDocAreaOpen
            ? '1px solid var(--curve-border, #E5E5E5)'
            : 'none',
          background: 'var(--curve-bg-white, #FFFFFF)',
        }}
      >
        {isDocAreaOpen && docArea}
      </div>
    </div>
  );
}
